/**
 * useNotifications — React Query hooks for notifications.
 * Includes polling every 60s for the notification list and unread count.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api/client';
import { notificationF9Endpoints } from '@/lib/api/endpoints-f9';
import type { AppNotification, PaginatedResponse, PaginationParams } from '@/lib/api/types';

// ============================================================
// Query Keys
// ============================================================

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (params?: PaginationParams) => [...notificationKeys.all, 'list', params] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
};

// ============================================================
// Queries
// ============================================================

/** Fetch paginated notification list with polling every 60s */
export function useNotificationsQuery(params?: PaginationParams) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () =>
      api.get<PaginatedResponse<AppNotification>>(notificationF9Endpoints.list(params)),
    refetchInterval: 60_000, // poll every 60 seconds
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      // Don't retry on auth errors (401/403) — the token is invalid/expired
      if (error instanceof ApiError && error.isAuthError) return false;
      return failureCount < 2;
    },
  });
}

/** Fetch unread notification count (used for badge in TopBar) */
export function useUnreadNotificationCountQuery() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const res = await api.get<PaginatedResponse<AppNotification>>(
        notificationF9Endpoints.list({ limit: 1 }),
      );
      // The API doesn't return a separate unread count, so we derive it from total
      // In a real app, the backend might return an unreadCount field
      return res.total;
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.isAuthError) return false;
      return failureCount < 2;
    },
    select: (data) => {
      // approximate unread count — in production the backend should return this
      return data;
    },
  });
}

// ============================================================
// Mutations
// ============================================================

/** Mark a single notification as read */
export function useMarkAsReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.put(notificationF9Endpoints.markAsRead(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** Mark all notifications as read */
export function useMarkAllAsReadMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.put(notificationF9Endpoints.markAllAsRead),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

/** Delete a notification */
export function useDeleteNotificationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(notificationF9Endpoints.delete(id)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}